/**
 * Logo Component
 * TCGEasy Jester/Joker Brand Logo
 * Supports multiple variants and sizes
 */

import styles from './Logo.module.css'

interface LogoProps {
  variant?: 'full' | 'icon' | 'wordmark'
  size?: 'sm' | 'md' | 'lg' | 'xl'
  color?: 'default' | 'white' | 'black'
  className?: string
  onClick?: () => void
}

export const Logo: React.FC<LogoProps> = ({ 
  variant = 'full',
  size = 'md',
  color = 'default',
  className = '',
  onClick 
}) => {
  // Construct image path based on variant and color
  const getLogoPath = () => {
    const colorSuffix = color === 'default' ? '' : `-${color}`
    
    switch (variant) {
      case 'icon':
        return `/images/logo/tcgeasy-icon${colorSuffix}.png`
      case 'wordmark':
        return `/images/logo/tcgeasy-wordmark${colorSuffix}.png`
      case 'full':
      default:
        return `/images/logo/tcgeasy-logo${colorSuffix}.png`
    }
  }

  // Size classes
  const sizeClass = styles[`size-${size}`] || styles['size-md']
  const variantClass = styles[`variant-${variant}`] || styles['variant-full']
  const colorClass = styles[`color-${color}`] || styles['color-default']
  const clickableClass = onClick ? styles.clickable : ''

  // Fallback: If image doesn't exist, show styled text logo
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    // Hide the image and show text fallback
    e.currentTarget.style.display = 'none'
    const parent = e.currentTarget.parentElement
    if (parent) {
      parent.classList.add(styles.useFallback)
    }
  }

  return (
    <div 
      className={`${styles.logo} ${sizeClass} ${variantClass} ${colorClass} ${clickableClass} ${className}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {/* SVG/PNG Logo Image */}
      <img 
        src={getLogoPath()}
        alt="TCGEasy"
        className={styles.logoImage}
        onError={handleImageError}
      />

      {/* Fallback: Styled Text Logo */}
      <div className={styles.fallbackLogo}>
        {variant === 'icon' ? (
          <span className={styles.iconFallback}>🃏</span>
        ) : variant === 'wordmark' ? (
          <span className={styles.wordmark}>TCGEasy</span>
        ) : (
          <>
            <span className={styles.jesterIcon}>🃏</span>
            <div className={styles.brandName}>
              <span className={styles.tcg}>TCG</span>
              <span className={styles.easy}>Easy</span>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default Logo
